/**
 * Отмена и восстановление заказа (order-cancel-restore; ТЗ п.1.5, п.9.3; ответ заказчицы В21) —
 * общее для клиента и команды:
 * — `CancelOrderDialog`: подтверждение текстом ТЗ «Вы уверены, что хотите отменить заказ?» и
 *   необязательная причина (её видит другая сторона);
 * — `RestoreOrderDialog`: подтверждение «Восстановить» — заказ вернётся в статус до отмены;
 * — `CancellationNotice`: плашка «Заказ отменён» — кем, когда, почему, и кнопка «Восстановить».
 * Отменить можно поданный и не завершённый заказ любой услуги; клиент восстанавливает только свою
 * отмену, команда — любую. Права приходят с сервера (`canCancel`, `canRestore`).
 */
import { useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Dialog, DialogClose, DialogContent } from '@/design-system/components/primitives/dialog'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Card } from '@/design-system/components/layout/card'
import { Alert } from '@/design-system/components/feedback/alert'
import { ordersApi, type OrderCancellation, type OrderDataStatus } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { TEAM_STATUS_LABELS } from '@/lib/orders'
import { cn } from '@/lib/utils'

/** Кто смотрит: клиент или команда Центрики. */
export type CancelSide = 'client' | 'team'

/** Текст подтверждения отмены — дословно из ТЗ (п.1.5). */
export const CANCEL_ORDER_QUESTION = 'Вы уверены, что хотите отменить заказ?'

/** Длина причины отмены — как на сервере. */
export const CANCEL_REASON_MAX = 1000

const TEXTAREA_CLASS =
  'w-full rounded-8 border border-line-strong bg-white px-3 py-2 text-14 text-txt outline-none transition-colors placeholder:text-txt-tertiary hover:border-line-strong focus-visible:ring-2 focus-visible:ring-accent'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Окно отмены заказа. `onConfirm` выполняет отмену (вызывающий закрывает окно по успеху); ошибка
 * показывается в окне, окно остаётся открытым. Пока идёт отмена, закрыть окно нельзя.
 */
export function CancelOrderDialog({
  open,
  side,
  orderNumber,
  onClose,
  onConfirm,
}: {
  open: boolean
  side: CancelSide
  orderNumber: string
  onClose: () => void
  onConfirm: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function close() {
    if (busy) return
    setReason('')
    setError(null)
    onClose()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await onConfirm(reason.trim())
      setReason('')
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отменить заказ'))
    } finally {
      setBusy(false)
    }
  }

  const title = `Отменить заказ № ${orderNumber}?`
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent title={title} description={CANCEL_ORDER_QUESTION} className="max-w-narrow">
        <Card
          header={
            <div className="flex items-center justify-between gap-3">
              <span>{title}</span>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Закрыть"
                  disabled={busy}
                  className={cn(
                    '-mr-1 rounded-8 p-1 text-txt-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    busy ? 'pointer-events-none opacity-50' : 'hover:bg-hover hover:text-txt',
                  )}
                >
                  <X className="size-4 stroke-current" aria-hidden />
                </button>
              </DialogClose>
            </div>
          }
        >
          <form onSubmit={submit} noValidate>
            <p className="mb-4 text-txt-secondary">{CANCEL_ORDER_QUESTION}</p>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}
            <Field
              label="Причина (необязательно)"
              hint={side === 'team' ? 'Причину увидит клиент' : 'Причину увидит менеджер Центрики'}
            >
              {(props) => (
                <textarea
                  {...props}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={CANCEL_REASON_MAX}
                  rows={3}
                  className={TEXTAREA_CLASS}
                />
              )}
            </Field>
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="submit" size="sm" variant="danger" disabled={busy}>
                {busy ? 'Отмена заказа…' : 'Отменить заказ'}
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={close}>
                Не отменять
              </Button>
            </div>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Подтверждение «Восстановить». `fromStatus` — статус, в который вернётся заказ (у карточки списка
 * его нет — общая формулировка). Ошибка показывается в окне.
 */
export function RestoreOrderDialog({
  open,
  fromStatus,
  onClose,
  onConfirm,
}: {
  open: boolean
  fromStatus?: OrderDataStatus
  onClose: () => void
  onConfirm: () => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function confirm() {
    setError(null)
    setBusy(true)
    try {
      await onConfirm()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось восстановить заказ'))
    } finally {
      setBusy(false)
    }
  }

  // Окно одно на все карточки списка: ошибка одного заказа не должна остаться в окне другого.
  function close() {
    setError(null)
    onClose()
  }

  return (
    <ConfirmDialog
      open={open}
      title="Восстановить заказ?"
      message={
        fromStatus
          ? `Заказ вернётся в статус «${TEAM_STATUS_LABELS[fromStatus]}».`
          : 'Заказ вернётся в статус, в котором был до отмены.'
      }
      details={error ? <Alert variant="danger">{error}</Alert> : undefined}
      buttons={[
        {
          label: busy ? 'Восстановление…' : 'Восстановить',
          variant: 'primary',
          disabled: busy,
          onClick: () => void confirm(),
        },
        { label: 'Не восстанавливать', variant: 'secondary', disabled: busy, onClick: close },
      ]}
      onClose={close}
      closeDisabled={busy}
    />
  )
}

/** Кем отменён заказ — для плашки и карточки списка. */
export function cancelledByText(c: OrderCancellation, side: CancelSide): string {
  if (side === 'client') return c.byClient ? 'Вы отменили заказ' : 'Заказ отменён Центрикой'
  if (c.byClient) return 'Заказ отменён клиентом'
  return c.byName ? `Заказ отменён Центрикой (${c.byName})` : 'Заказ отменён Центрикой'
}

/** Плашка «Заказ отменён»: кем, когда, почему; «Восстановить» — если можно. */
export function CancellationNotice({
  cancellation,
  side,
  canRestore,
  onRestore,
  className,
}: {
  cancellation: OrderCancellation
  side: CancelSide
  canRestore: boolean
  onRestore: () => void
  className?: string
}) {
  return (
    <Alert
      variant="info"
      title="Заказ отменён"
      className={className}
      actions={
        canRestore ? (
          <Button size="sm" variant="secondary" onClick={onRestore}>
            Восстановить
          </Button>
        ) : undefined
      }
    >
      <p>
        {cancelledByText(cancellation, side)} {formatDate(cancellation.at)}.
      </p>
      <p className="mt-1">
        {cancellation.reason ? `Причина: ${cancellation.reason}` : 'Причина не указана.'}
      </p>
      {side === 'client' && !canRestore ? (
        <p className="mt-1 text-txt-secondary">
          Если заказ отменён по ошибке, свяжитесь с вашим менеджером — он восстановит заказ.
        </p>
      ) : null}
    </Alert>
  )
}

/**
 * Анкета отменённого заказа (открыта по прямому адресу): только просмотр, подробности об отмене и
 * «Восстановить» — на странице заказа.
 */
export function CancelledOrderAlert({
  orderId,
  className,
}: {
  orderId: string
  className?: string
}) {
  const navigate = useNavigate()
  return (
    <Alert
      variant="info"
      title="Заказ отменён"
      className={className}
      actions={
        <Button size="sm" variant="secondary" onClick={() => navigate(`/orders/${orderId}`)}>
          Открыть заказ
        </Button>
      }
    >
      Анкета доступна только для чтения. Кем, когда и почему отменён заказ — на странице заказа.
    </Alert>
  )
}

/**
 * «Отменить заказ» в анкете услуги: заказ «На заполнении» и «Требуются правки» открывается сразу в
 * анкете, минуя страницу заказа, — кнопка отмены нужна и здесь (ТЗ п.1.5). Права — с сервера
 * (`canCancel`); черновик не отменяется, его удаляют. После отмены — страница заказа: там видно,
 * кто и когда отменил, и «Восстановить».
 */
export function AnketaCancelOrderButton({
  order,
  onCancelled,
}: {
  order: { id: string; number: string; dataStatus: OrderDataStatus; canCancel?: boolean }
  /** Отмена прошла (обновить счётчики кабинета). */
  onCancelled?: () => void
}) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const inDataPhase = order.dataStatus === 'filling' || order.dataStatus === 'changes_requested'
  if (!order.canCancel || !inDataPhase) return null

  async function confirm(reason: string) {
    // Отказ сервера (заказ уже отменили или отправили с другой стороны) — ошибка в окне.
    await ordersApi.cancel(order.id, reason)
    setOpen(false)
    onCancelled?.()
    navigate(`/orders/${order.id}`, { replace: true })
  }

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Отменить заказ
      </Button>
      <CancelOrderDialog
        open={open}
        side="client"
        orderNumber={order.number}
        onClose={() => setOpen(false)}
        onConfirm={confirm}
      />
    </>
  )
}
